/* ========================================================================== *\
___________       __         .__
\_   _____/ _____/  |________|__| ____
 |    __)_ /    \   __\_  __ \  |/  _ \
 |        \   |  \  |  |  | \/  (  <_> )
/_______  /___|  /__|  |__|  |__|\____/
        \/     \/
\* ========================================================================== */
// เลือกรูปจากระบบ (รูปสินค้า / รูปแคมเปญ) ให้หน้าปรับสเกลภาพ (A7 · 25 ต.ค. 2569)
//   ใช้ endpoint อ่านอย่างเดียวของหน้าดาวน์โหลดรูป: OPTIONS /image_export/products|campaigns|files
//   onPick(items) → [{ url, name, from: {kind, id, code} }] · ไม่เขียนฐาน (from ใช้ตอน "เก็บกลับเข้า" สินค้า/แคมเปญ)
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import { FetchData } from 'services/Request';

const PER_PAGE = 12;

export default function SystemImagePicker({ open, onClose, onPick, token }) {
  const [tab, setTab] = useState(0);
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  /* แบ่งหน้าผลค้นหา (รีเช็ค 26 ต.ค.: เดิมเห็นแค่ 12 รายการแรก คำค้นกว้างอย่าง "แชมพู" หาตัวที่ 13 ไม่เจอ) */
  const [page, setPage] = useState(1);
  const [found, setFound] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chosen, setChosen] = useState(null); // แถวที่เลือก (สินค้า/แคมเปญ)
  const [files, setFiles] = useState([]);
  const [ticked, setTicked] = useState({});
  const timer = useRef(null);
  const reqId = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      clearTimeout(timer.current);
    };
  }, []);

  const search = (which, text, pg = 1) => {
    reqId.current += 1;
    const id = reqId.current;
    setLoading(true);
    setPage(pg);
    FetchData('OPTIONS', `/image_export/${which === 0 ? 'products' : 'campaigns'}`, token, {
      search: text,
      page: pg,
      limit: PER_PAGE,
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current || id !== reqId.current) return;
        setLoading(false);
        setRows(r.status === 'ok' ? r.data || [] : []);
        setFound(r.status === 'ok' ? Number(r.total || 0) : 0);
      })
      .catch(() => {
        if (alive.current && id === reqId.current) setLoading(false);
      });
  };

  const chooseReq = useRef(0); // เลขคำขอรายการรูป — เปลี่ยนแท็บ/เปิดใหม่ต้องทิ้งคำตอบเก่า (รีเช็ค 27 ต.ค. 2569)
  useEffect(() => {
    if (!open) return;
    chooseReq.current += 1;
    setTab(0);
    setQ('');
    setChosen(null);
    setFiles([]);
    setTicked({});
    search(0, '');
  }, [open]);

  const changeTab = (v) => {
    clearTimeout(timer.current);
    chooseReq.current += 1;
    setTab(v);
    setQ('');
    setChosen(null);
    setFiles([]);
    setTicked({});
    search(v, '');
  };
  const changeQ = (v) => {
    setQ(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => search(tab, v), 400);
  };
  const [note, setNote] = useState('');
  const choose = (row) => {
    chooseReq.current += 1;
    const id = chooseReq.current; // กด A แล้ว B เร็ว ๆ — คำตอบของ A ที่มาทีหลังต้องไม่ไปโผล่ใต้หัว B
    setChosen(row);
    setFiles([]);
    setTicked({});
    setNote('');
    setLoading(true);
    FetchData('OPTIONS', '/image_export/files', token, {
      [tab === 0 ? 'product_ids' : 'campaign_ids']: [row.id],
      product_images: 'Y',
      campaign_images: 'Y',
      _t: Date.now(),
    })
      .then((r) => {
        if (!alive.current || id !== chooseReq.current) return;
        setLoading(false);
        setFiles(r.status === 'ok' ? r.files || [] : []);
        if (r.status !== 'ok')
          setNote(
            r.msg === 'export_too_many_files' || r.msg === 'export_too_large'
              ? 'รายการนี้มีรูปมากเกินไป — เลือกเป็นสินค้าทีละตัวแทน'
              : 'โหลดรายการรูปไม่สำเร็จ ลองใหม่อีกครั้ง'
          );
      })
      .catch(() => {
        if (alive.current && id === chooseReq.current) {
          setLoading(false);
          setNote('โหลดรายการรูปไม่สำเร็จ ลองใหม่อีกครั้ง');
        }
      });
  };
  const confirm = () => {
    const from = chosen
      ? {
          kind: tab === 0 ? 'product' : 'campaign',
          id: chosen.id,
          code: tab === 0 ? chosen.sku : chosen.code,
        }
      : null;
    onPick(files.filter((f) => ticked[f.url]).map((f) => ({ url: f.url, name: f.name, from })));
    onClose();
  };
  const nTicked = Object.values(ticked).filter(Boolean).length;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <DialogTitle>เลือกรูปจากระบบ</DialogTitle>
      <DialogContent dividers>
        <Tabs value={tab} onChange={(e, v) => changeTab(v)} aria-label='แหล่งรูป'>
          <Tab label='รูปสินค้า' />
          <Tab label='รูปแคมเปญ' />
        </Tabs>
        <MDBox display='flex' gap={2} mt={2} flexWrap='wrap'>
          <MDBox flex='1 1 260px' minWidth={0}>
            <MDInput
              size='small'
              fullWidth
              placeholder={tab === 0 ? 'ค้นรหัสหรือชื่อสินค้า' : 'ค้นรหัสหรือชื่อแคมเปญ'}
              value={q}
              onChange={(e) => changeQ(e.target.value)}
              inputProps={{ 'aria-label': 'ค้นหาในระบบ' }}
            />
            <MDBox mt={1} sx={{ maxHeight: 360, overflowY: 'auto' }}>
              {rows.map((r) => {
                const code = tab === 0 ? r.sku : r.code;
                const active = chosen && chosen.id === r.id;
                return (
                  <MDBox
                    key={r.id}
                    role='button'
                    tabIndex={0}
                    onClick={() => choose(r)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault(); // Space ไม่ให้เลื่อนรายการ
                        choose(r);
                      }
                    }}
                    display='flex'
                    alignItems='center'
                    gap={1}
                    sx={{
                      p: 0.75,
                      borderRadius: 1,
                      cursor: 'pointer',
                      bgcolor: active ? 'info.main' : 'transparent',
                      color: active ? '#fff' : 'inherit',
                      '&:hover': { bgcolor: active ? 'info.main' : 'grey.100' },
                    }}
                  >
                    {r.image_url ? (
                      <MDBox
                        component='img'
                        src={r.image_url}
                        alt=''
                        sx={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 1 }}
                      />
                    ) : (
                      <MDBox sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'grey.200' }} />
                    )}
                    <MDBox minWidth={0}>
                      <MDTypography variant='button' fontWeight='medium' color='inherit'>
                        {code}
                      </MDTypography>
                      <MDTypography
                        variant='caption'
                        color='inherit'
                        display='block'
                        sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                        title={r.name}
                      >
                        {r.name} · {r.img_count} รูป
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                );
              })}
              {!loading && !rows.length && (
                <MDTypography variant='caption' color='text'>
                  ไม่พบรายการ
                </MDTypography>
              )}
              {found > PER_PAGE && (
                <MDBox display='flex' alignItems='center' gap={1} mt={0.5}>
                  <MDButton
                    size='small'
                    variant='text'
                    color='dark'
                    disabled={loading || page <= 1}
                    onClick={() => search(tab, q, page - 1)}
                  >
                    ก่อนหน้า
                  </MDButton>
                  <MDTypography variant='caption' color='text'>
                    หน้า {page}/{Math.ceil(found / PER_PAGE)} · {found} รายการ
                  </MDTypography>
                  <MDButton
                    size='small'
                    variant='text'
                    color='dark'
                    disabled={loading || page >= Math.ceil(found / PER_PAGE)}
                    onClick={() => search(tab, q, page + 1)}
                  >
                    ถัดไป
                  </MDButton>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
          <MDBox flex='1 1 300px' minWidth={0}>
            <MDTypography variant='caption' fontWeight='bold' display='block' mb={1}>
              {chosen
                ? `รูปของ ${tab === 0 ? chosen.sku : chosen.code} · ติ๊กที่จะใช้`
                : 'เลือกสินค้าหรือแคมเปญทางซ้ายก่อน'}
            </MDTypography>
            {loading && <CircularProgress size={20} />}
            <MDBox display='flex' flexWrap='wrap' gap={1}>
              {files.map((f) => (
                <MDBox
                  key={f.url}
                  sx={{
                    position: 'relative',
                    width: 96,
                    height: 96,
                    borderRadius: 1,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: ticked[f.url] ? 'info.main' : 'grey.300',
                  }}
                >
                  <MDBox
                    component='img'
                    src={f.url}
                    alt={f.name}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <Checkbox
                    size='small'
                    checked={Boolean(ticked[f.url])}
                    onChange={(e) => setTicked({ ...ticked, [f.url]: e.target.checked })}
                    inputProps={{ 'aria-label': `เลือกรูป ${f.name}` }}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      bgcolor: 'rgba(255,255,255,.8)',
                      p: 0.25,
                    }}
                  />
                </MDBox>
              ))}
              {chosen && !loading && !files.length && (
                <MDTypography variant='caption' color={note ? 'error' : 'text'}>
                  {note || 'ไม่มีไฟล์รูป'}
                </MDTypography>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </DialogContent>
      <DialogActions>
        <MDButton variant='outlined' color='secondary' onClick={onClose}>
          ยกเลิก
        </MDButton>
        <MDButton variant='gradient' color='dark' disabled={!nTicked} onClick={confirm}>
          ใช้ {nTicked || ''} รูป
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

SystemImagePicker.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onPick: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired,
};
